import {Modal, Offcanvas} from "bootstrap";
import Lenis from 'lenis'; // Smooth scroll
import lozad from 'lozad'; // Lazy load
import imagesLoaded from 'imagesloaded';
import "./swiperHandler"; // Swiper by data-attribute
import mainNavHandler from "./mainNavHandler";
import {matchMediaMDUp} from "./mediaQuery";
import TomSelect from 'tom-select';
import scrollObserver from "./scrollObserver";
import {startPageOnTop} from "./utilities";
import {debounce} from "./debounce";
import {gsap} from "gsap";
import {SplitText} from "gsap/SplitText";
import stringReveal from "./string-reveal";

/* Swiper | begin */
import {register} from 'swiper/element/bundle';
import {loadModuleIfExists} from "./helpers"
import {ScrollTrigger} from "gsap/ScrollTrigger";

register();
/* Swiper end */

// Alpine.plugin(intersect)

document.addEventListener('DOMContentLoaded', async () => {
    startPageOnTop();

    /**
     * Title animation
     */
    stringReveal(gsap, SplitText);

    // Initialize Lenis
    const lenis = new Lenis({
        autoRaf: true,
    });

    window.lenis = lenis

    lenis.on('scroll', (e) => {
        if (e.scroll <= 20) {
            document.body.removeAttribute('data-scroll-direction')
            return
        }

        if (e.direction === 1) {
            document.body.dataset.scrollDirection = 'down'
        } else if (e.direction === -1) {
            document.body.dataset.scrollDirection = 'up'
        }
    })

    /**
     * Lazy load
     */
    window.lazyLoader = () => {
        const lazyItems = document.querySelectorAll('.lazy');
        if (lazyItems.length) {
            let options = {
                loaded: function (el) {
                    imagesLoaded(el, function () {
                        el.parentNode.classList.add('media-loaded');
                    });
                    if (!el.alt && el.dataset.alt) {
                        el.alt = el.dataset.alt;
                    }
                }
            };
            const observer = lozad(lazyItems, options);
            observer.observe();
        }
    }

    lazyLoader();


    /**
     * Detect js disabled
     */
    if (document.body.classList.contains('no-js')) document.body.classList.remove('no-js');


    /**
     * Main Nav
     */
    const mainNav = document.getElementById('main-nav');
    const mainNavLinks = document.querySelectorAll('.nav-link--main');
    if (mainNav && mainNavLinks?.length) {
        mainNavHandler(mainNav, mainNavLinks)
    }

    /**
     * Anchors nav
     */
    const anchorsNav = document.getElementById('collapseAnchorsNav');
    if (anchorsNav) {
        document.addEventListener('scroll', () => {
            if (anchorsNav.classList.contains('show')) {
                document.querySelector(`button[data-bs-toggle="collapse"]`).click()
            }
        })
    }

    /**
     * Footer menu toggles
     * @type {NodeListOf<Element>}
     */
    const mainFooterMenuToggles = document.querySelectorAll('.main-footer__menu-toggle.has-children');
    if (mainFooterMenuToggles?.length) {
        mainFooterMenuToggles.forEach(btn => {
            const menu = document.getElementById(btn.getAttribute('aria-controls'));
            menu.hidden = !matchMediaMDUp.matches;

            btn.addEventListener('click', e => {
                const expanded = btn.getAttribute('aria-expanded') === 'true';
                btn.setAttribute('aria-expanded', !expanded);
                menu.hidden = expanded;
            });
        });
    }

    /**
     * Scroll reveal
     */
    const revealItemsContainer = document.querySelectorAll('[data-reveal]');
    if (revealItemsContainer?.length) {
        scrollObserver({items: revealItemsContainer})
    }

    /**
     * Scroll fade-in
     */
    const fadeInItems = document.querySelectorAll('[data-fade-in]');
    if (fadeInItems?.length) {
        scrollObserver({items: fadeInItems})
    }

    /**
     * In viewport autoplay carousels
     */
    const inViewportAutoplayCarousels = document.querySelectorAll('.carousel-container.in-viewport-autoplay');
    if (inViewportAutoplayCarousels?.length) {
        const handleCarouselIntersecting = (entry, isIntersecting) => {
            const swiper = entry.target.querySelector('.swiper');
            const swiperInstance = swiper?.swiperInstance;
            if (!swiperInstance || !swiperInstance.autoplay) {
                return;
            }

            if (isIntersecting) {
                swiper.parentNode.classList.add('animated')
                swiperInstance.autoplay.start();
            } else {
                swiper.parentNode.classList.remove('animated')
                swiperInstance.autoplay.stop()
            }
        }

        scrollObserver({items: inViewportAutoplayCarousels}, handleCarouselIntersecting);
    }

    /**
     * Scroll background
     * @type {NodeListOf<Element>}
     */
    const scrollBackgroundItems = document.querySelectorAll('[data-scroll-background]');
    if (scrollBackgroundItems?.length) {
        const handleBackgroundIntersecting = (entry, isIntersecting) => {
            if (!isIntersecting) return;
            const scrollBackground = entry.target.dataset.scrollBackground;
            if (!scrollBackground) return;
            entry.target.style.setProperty('--section-bg-color', scrollBackground);
        };
        scrollObserver({items: scrollBackgroundItems}, handleBackgroundIntersecting)
    }

    /**
     * Brands
     * @type {Element}
     */
    await loadModuleIfExists('.brands', () => import('./brands'))

    /**
     * Sostenibilita
     */
    await loadModuleIfExists('.sostenibilita', () => import('./sostenibilita'))

    /**
     * Home
     */
    await loadModuleIfExists('.home', () => import('./home'))

    /**
     * Fondazione Ermanno Casoli
     */
    await loadModuleIfExists('.fondazione-ermanno-casoli', () => import('./fondazione-ermanno-casoli'))

    /**
     * Chi siamo
     */
    await loadModuleIfExists('.chi-siamo', () => import('./chi-siamo'))

    /**
     * Carriere
     */
    await loadModuleIfExists('.carriere', () => import('./carriere'))

    /**
     * Vertical slider
     */
    await loadModuleIfExists('.carriere-vertical-slider', () => import('./verticalSlider'))
    await loadModuleIfExists('.storia-vertical-slider', () => import('./verticalSlider'))

    /**
     * Animated numbers
     */
    await loadModuleIfExists('.animated-number', () => import('./animatedNumber'))

    /**
     * Storia
     */
    await loadModuleIfExists('.storia', () => import('./storia'))

    // const horSlider = document.querySelector('.horizontal-slider');

    /**
     * Body class by desktop height
     */
    const handleHeightClass = () => {
        const threshold = 960;
        const isSmallHeight = window.innerHeight < threshold;
        document.body.classList.toggle('small-height-desktop', isSmallHeight);
    };

    handleHeightClass();
    const debouncedResize = debounce(handleHeightClass, 250);
    window.addEventListener('resize', debouncedResize);
});

window.setTomSelect = function (el, options = {}, classes = '') {
    if (!el) return;

    options = options ?? {};

    const select = document.querySelector(el);
    if (!select) return;

    const instance = select.tomselect;
    let currentValue = null;

    if (instance) {
        currentValue = instance.getValue();
        instance.destroy();
    }

    const defaultOptions = {
        create: false,
        searchField: [],
        controlInput: null,
        openOnFocus: true,
        onInitialize() {
            this.wrapper?.classList.add(classes); // extra classes
            this.dropdown.setAttribute('data-lenis-prevent', ''); // consente lo scroll in presenza di lenis
        }
    };

    // Usa prima le defaultOptions, poi l'override (se presente)
    const defaultOnInit = defaultOptions.onInitialize;
    const customOnInit = options.onInitialize;

    const mergedOptions = {
        ...defaultOptions,
        ...options,
        onInitialize() {
            defaultOnInit.call(this);
            customOnInit?.call(this);
        }
    };

    const ts = new TomSelect(el, mergedOptions);

    if (currentValue) {
        ts.setValue(currentValue, true);
    }
}
